import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router';
import { Layout } from './components/Layout';
import { articlesIndex } from './data';
import { CategoryEntry, LegacyArticle } from './routes/Article';
import { CategoryPage } from './routes/CategoryPage';
import { DviDemo } from './routes/DviDemo';
import { Editor } from './routes/Editor';
import { Home } from './routes/Home';
import { Licenses } from './routes/Licenses';
import { LinkPage } from './routes/LinkPage';
import { NotFound } from './routes/NotFound';
import { SolutionsProblem } from './routes/SolutionsProblem';
import { SolutionsWork } from './routes/SolutionsWork';
import { scheduleRendererWarmup } from './dvi/warmup';
import './styles/global.css';

/** 旧 /bourbaki/:book/* → /solutions/bourbaki/:book/* (URL 互換)。 */
function LegacyBourbakiRedirect() {
  const params = useParams<{ book: string; '*': string }>();
  return <Navigate to={`/solutions/bourbaki/${params.book}/${params['*'] ?? ''}`} replace />;
}

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      { path: '/', element: <Home /> },
      // カテゴリ (sections.toml) ごとにトップページと子要素/記事のルートを張る
      ...articlesIndex.categories.flatMap((c) => [
        { path: `/${c.key}`, element: <CategoryPage categoryKey={c.key} /> },
        { path: `/${c.key}/:key`, element: <CategoryEntry categoryKey={c.key} /> },
      ]),
      // 旧 URL 互換: /logic と旧トピック別子要素は小噺へ、/logic/:slug は記事のまま
      { path: '/logic', element: <Navigate to="/math/stories" replace /> },
      { path: '/logic/:slug', element: <LegacyArticle section="logic" /> },
      ...['ring-theory', 'topology', 'category-theory', 'constructive-math'].map((key) => ({
        path: `/math/${key}`,
        element: <Navigate to="/math/stories" replace />,
      })),
      // 解答集 (作品トップと問題ページ)
      { path: '/solutions/:work', element: <SolutionsWork /> },
      { path: '/solutions/:work/*', element: <SolutionsProblem /> },
      // 旧 URL 互換 (解答集の一般化前)
      { path: '/math/bourbaki', element: <Navigate to="/math/solutions" replace /> },
      { path: '/bourbaki/:book/*', element: <LegacyBourbakiRedirect /> },
      { path: '/link', element: <LinkPage /> },
      { path: '/licenses', element: <Licenses /> },
      { path: '/dev/dvi', element: <DviDemo /> },
      // ブロックエディタ (案A)。dev ビルドのみ有効 (バックエンド API も dev 限定)。
      ...(import.meta.env.DEV ? [{ path: '/dev/editor', element: <Editor /> }] : []),
      { path: '*', element: <NotFound /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);

// どのページでも、表示が落ち着いたら wasm とフォントを先読みしておく
scheduleRendererWarmup();

// DVI レンダラ資産 (wasm/フォント/DVI) のキャッシュ (spec/02)。
// dev では HMR と干渉するため本番ビルドのみ登録する
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e: unknown) => {
      console.warn('service worker registration failed:', e);
    });
  });
}
